import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UsersFour, Plus, Trash, PencilSimple } from "@phosphor-icons/react";

/**
 * Section "Assistants multi-praticiens" — visible seulement pour les
 * établissements. Permet de créer des assistants/research users et de
 * choisir à quels praticiens (cabinets) ils ont accès.
 */
export default function OrgAssistantsSection({ isEstablishment }) {
  const [assistants, setAssistants] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', assistant? }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/org/assistants");
      setAssistants(data.assistants || []);
      setServices(data.services || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Chargement impossible");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isEstablishment) load(); }, [isEstablishment]);

  const del = async (a) => {
    if (!window.confirm(`Retirer ${a.email} de l'établissement ?`)) return;
    try {
      await api.delete(`/org/assistants/${a.user_id}`);
      toast.success("Assistant retiré");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec"); }
  };

  if (!isEstablishment) return null;

  return (
    <div className="bg-white border border-[#E1E9F0] rounded-2xl p-6" data-testid="assistants-section">
      <div className="flex items-center gap-3 mb-4">
        <UsersFour size={20} weight="duotone" className="text-[#3A8FA5]" />
        <h3 className="text-[16px] font-bold text-[#1E3A5F]">Assistants & recherche ({assistants.length})</h3>
        <span className="text-[12px] text-[#5F758F]">— accès personnalisé par praticien</span>
        <Button size="sm" className="ml-auto rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold"
          onClick={() => setDialog({ mode: "create" })} data-testid="add-assistant-btn">
          <Plus size={14} className="mr-1.5" /> Ajouter
        </Button>
      </div>
      {loading ? (
        <p className="text-sm text-[#5F758F]">Chargement…</p>
      ) : assistants.length === 0 ? (
        <p className="text-sm text-[#5F758F] italic">Aucun assistant multi-praticien pour l'instant.</p>
      ) : (
        <div className="border border-[#E1E9F0] rounded-[10px] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F7FB] text-[#5F758F]"><tr>
              <th className="text-left px-4 py-2.5 font-semibold">Nom · Email</th>
              <th className="text-left px-4 py-2.5 font-semibold">Rôle</th>
              <th className="text-left px-4 py-2.5 font-semibold">Praticiens accessibles</th>
              <th className="px-4 py-2.5"></th>
            </tr></thead>
            <tbody>
              {assistants.map((a) => {
                const cabs = a.accessible_cabinet_ids || [];
                const cabNames = cabs.map((cid) => services.find((s) => s.cabinet_id === cid)?.cabinet_name || services.find((s) => s.cabinet_id === cid)?.name || "?");
                return (
                  <tr key={a.user_id} className="border-t border-[#E1E9F0]" data-testid={`assistant-${a.user_id}`}>
                    <td className="px-4 py-2.5">
                      <p className="text-[#1E3A5F] font-medium">{a.name}</p>
                      <p className="text-[12px] text-[#5F758F]">{a.email}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge className={a.role === "research" ? "bg-purple-100 text-purple-800 hover:bg-purple-100" : "bg-[#E6F1F5] text-[#3A8FA5] hover:bg-[#E6F1F5]"}>
                        {a.role === "research" ? "Recherche" : "Assistant"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {cabNames.map((n, i) => (
                          <span key={i} className="text-[11px] bg-[#EEF3F8] text-[#1E3A5F] px-2 py-0.5 rounded-full border border-[#E1E9F0]">{n}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => setDialog({ mode: "edit", assistant: a })} data-testid={`edit-assistant-${a.user_id}`}>
                        <PencilSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => del(a)} className="text-red-600 hover:bg-red-50" data-testid={`delete-assistant-${a.user_id}`}>
                        <Trash size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dialog && (
        <AssistantDialog
          mode={dialog.mode} assistant={dialog.assistant}
          services={services} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }}
        />
      )}
    </div>
  );
}

function AssistantDialog({ mode, assistant, services, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [email, setEmail] = useState(assistant?.email || "");
  const [name, setName] = useState(assistant?.name || "");
  const [role, setRole] = useState(assistant?.role || "assistant");
  const [password, setPassword] = useState("");
  const [selected, setSelected] = useState(new Set(assistant?.accessible_cabinet_ids || []));
  const [busy, setBusy] = useState(false);

  const toggle = (cid) => {
    setSelected((prev) => {
      const s = new Set(prev);
      if (s.has(cid)) s.delete(cid); else s.add(cid);
      return s;
    });
  };

  const submit = async () => {
    if (selected.size === 0) { toast.error("Sélectionnez au moins un praticien"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/org/assistants/${assistant.user_id}`, {
          name, role,
          accessible_cabinet_ids: Array.from(selected),
        });
        toast.success("Assistant mis à jour");
      } else {
        if (!email || password.length < 8) {
          toast.error("Email + mot de passe (8+ car.) obligatoires");
          setBusy(false); return;
        }
        await api.post("/org/assistants", {
          email, name, role, password,
          accessible_cabinet_ids: Array.from(selected),
        });
        toast.success("Assistant créé");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="bg-white sm:max-w-lg" data-testid="assistant-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#1E3A5F]">
            {isEdit ? `Modifier ${assistant.email}` : "Ajouter un assistant"}
          </DialogTitle>
          <DialogDescription className="text-[#5F758F]">
            Choisissez les praticiens dont il/elle pourra voir les patients, envois et réponses.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {!isEdit && (
            <>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Email</Label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="assistant@clinique.fr"
                  className="mt-2 rounded-[10px]" data-testid="assistant-email" />
              </div>
              <div>
                <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Mot de passe temporaire (8+ caractères)</Label>
                <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password"
                  className="mt-2 rounded-[10px]" data-testid="assistant-password" />
                <p className="text-[11px] text-[#5F758F] mt-1">L'utilisateur pourra le modifier à sa première connexion.</p>
              </div>
            </>
          )}
          <div>
            <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Nom complet</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-[10px]" data-testid="assistant-name" />
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Rôle</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-2 rounded-[10px]" data-testid="assistant-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="assistant">Assistant (patients + envois + réponses)</SelectItem>
                <SelectItem value="research">Recherche (études + réponses)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-[0.12em] uppercase text-[#5F758F] font-semibold">Praticiens accessibles ({selected.size})</Label>
            <div className="mt-2 space-y-1 max-h-52 overflow-auto border border-[#E1E9F0] rounded-[10px] p-2">
              {services.length === 0 && <p className="text-sm text-[#5F758F] italic p-2">Aucun praticien dans l'établissement.</p>}
              {services.map((s) => (
                <label key={s.cabinet_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F4F7FB] cursor-pointer">
                  <input type="checkbox" checked={selected.has(s.cabinet_id)} onChange={() => toggle(s.cabinet_id)}
                    className="w-4 h-4 accent-[#3A8FA5]" data-testid={`cab-${s.cabinet_id}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1E3A5F] font-medium">{s.cabinet_name || s.name}</p>
                    <p className="text-[11px] text-[#5F758F]">{s.email}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#3A8FA5] hover:bg-[#164E5F] text-white" data-testid="assistant-save">
            {busy ? "…" : isEdit ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
